// WW-162 (Vania 13 Oct 2026 · D-4 QA) · one Nairobi-time helper.
//
// Problem: `new Date("2026-10-14T09:00:00")` (no tz suffix) is parsed in
// the viewer's local timezone per the ECMAScript spec, so a California
// browser renders every zoneless EAT string 10 h late even if the
// formatter is told `timeZone: "Africa/Nairobi"`.
//
// Rule: a zoneless ISO string is Africa/Nairobi (+03:00). All desk +
// member surfaces call this helper so the moment is pinned to EAT
// BEFORE any formatter runs.

const EAT_SUFFIX = "+03:00";
export const NAIROBI_TZ = "Africa/Nairobi";

// Zoneless pattern: "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss" with
// optional fractional seconds, NOT ending in Z, +hh:mm, +hhmm, -hh:mm,
// -hhmm.
const ZONELESS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(?:\.\d+)?)?$/;

export function hasZone(iso) {
  if (!iso || typeof iso !== "string") return false;
  // Z suffix or ±hh:mm / ±hhmm.
  return /Z$/i.test(iso) || /[+\-]\d{2}:?\d{2}$/.test(iso);
}

/**
 * Coerce a zoneless ISO string to EAT. Strings that already carry a
 * zone are returned unchanged. Non-strings fall through so the caller
 * can decide how to handle them.
 */
export function ensureNairobi(iso) {
  if (!iso || typeof iso !== "string") return iso;
  if (hasZone(iso)) return iso;
  if (ZONELESS_RE.test(iso)) return iso + EAT_SUFFIX;
  return iso;
}

/**
 * Build a Date pinned to the correct moment. Safe to call with any
 * ISO string; returns `null` for empty / unparseable input.
 */
export function nairobiDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(ensureNairobi(iso));
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Format a moment in Africa/Nairobi regardless of the viewer's zone.
 * `options` are standard `Intl.DateTimeFormat` options.
 */
export function formatNairobi(iso, options = {}) {
  const d = nairobiDate(iso);
  if (!d) return "";
  return d.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, ...options });
}

/**
 * "wed 14 oct · 8:00am" style line used on cards. Lowercase, no commas,
 * minutes dropped when they're zero. Caller still owns the venue/dash
 * composition.
 */
export function formatCardWhen(iso) {
  const d = nairobiDate(iso);
  if (!d) return "date TBA";
  const day = d.toLocaleString("en-GB", {
    timeZone: NAIROBI_TZ, day: "2-digit", month: "short",
  }).toLowerCase();
  const time = d.toLocaleString("en-GB", {
    timeZone: NAIROBI_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  });
  return `${day}, ${time}`;
}

// WW-160.9 · ISO → "YYYY-MM-DDTHH:mm" in Nairobi time for <input type="datetime-local">.
export function toNairobiInput(iso) {
  const d = nairobiDate(iso);
  if (!d) return "";
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: NAIROBI_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).map((x) => [x.type, x.value]));
  const hh = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hh}:${p.minute}`;
}

export const fromNairobiInput = (v) => (v ? `${v.slice(0, 16)}:00${EAT_SUFFIX}` : "");

// WW-161 · the member event page date line (night rule + multi-day fold).
export function formatDateLine(startIso, endIso) {
  const s = nairobiDate(startIso);
  if (!s) return startIso || "TBA";
  const e = endIso ? nairobiDate(endIso) : null;
  const fmtTime = (d) => {
    const t = d.toLocaleString("en-GB", {
      timeZone: NAIROBI_TZ, hour: "numeric", minute: "2-digit", hour12: true,
    }).toLowerCase().replace(" ", "");
    return t.replace(/^0:(\d{2})(am|pm)$/, "12:$1$2");
  };
  const stWeekdayLong = s.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, weekday: "long" }).toLowerCase();
  const stWeekdayShort = s.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, weekday: "short" }).toLowerCase();
  const stDay = s.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, day: "numeric" });
  const stMonth = s.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, month: "long" }).toLowerCase();
  const stTime = fmtTime(s);
  if (!e) return `${stWeekdayLong} ${stDay} ${stMonth} · ${stTime}`;
  const etTime = fmtTime(e);
  const nairobiYMD = (d) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: NAIROBI_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d);
    const y = parseInt(parts.find((p) => p.type === "year").value, 10);
    const m = parseInt(parts.find((p) => p.type === "month").value, 10);
    const dd = parseInt(parts.find((p) => p.type === "day").value, 10);
    return Date.UTC(y, m - 1, dd);
  };
  const dayDiff = Math.round((nairobiYMD(e) - nairobiYMD(s)) / 86_400_000);
  let eHour = parseInt(e.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, hour: "2-digit", hour12: false }), 10);
  if (!Number.isFinite(eHour) || eHour === 24) eHour = 0;
  let eMin = parseInt(e.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, minute: "2-digit" }), 10);
  if (!Number.isFinite(eMin)) eMin = 0;
  const endsInEarlyMorning = eHour * 60 + eMin <= 360 && dayDiff >= 1;
  const effectiveDayDiff = endsInEarlyMorning ? dayDiff - 1 : dayDiff;
  if (effectiveDayDiff <= 0) {
    return `${stWeekdayLong} ${stDay} ${stMonth} · ${stTime} – ${etTime}`;
  }
  const endDisplay = endsInEarlyMorning ? new Date(e.getTime() - 86_400_000) : e;
  const etWeekdayShort = endDisplay.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, weekday: "short" }).toLowerCase();
  const etDay = endDisplay.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, day: "numeric" });
  const etMonth = endDisplay.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, month: "long" }).toLowerCase();
  if (stMonth === etMonth) {
    return `${stWeekdayShort} ${stDay} to ${etWeekdayShort} ${etDay} ${stMonth} · ${stTime} – ${etTime}`;
  }
  return `${stWeekdayShort} ${stDay} ${stMonth} to ${etWeekdayShort} ${etDay} ${etMonth} · ${stTime} – ${etTime}`;
}

